/**
 * ResizeObserver 浏览器噪音兜底
 *
 * 层级：utils（全局运行时补丁）
 *
 * 职责：
 * 1. 尽早 patch 当前 window / micro-app rawWindow 的 ResizeObserver
 * 2. 将 ResizeObserver 回调延迟到 requestAnimationFrame，避开同步布局测量/更新循环
 * 3. 只吞掉浏览器已知的 ResizeObserver loop 噪音错误
 *
 * 不关心具体业务组件；用于兼容 antd/rc-resize-observer 在浮层打开关闭时的浏览器提示。
 */

const RESIZE_OBSERVER_LOOP_ERRORS = new Set([
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
]);

const patchedWindows = new WeakSet<Window>();

const isResizeObserverLoopError = (message?: string) =>
  !!message && RESIZE_OBSERVER_LOOP_ERRORS.has(message);

const getTargetWindows = (): Window[] => {
  if (typeof window === 'undefined') return [];
  const currentWindow = window as Window & { rawWindow?: Window };
  return Array.from(
    new Set(
      [window, currentWindow.rawWindow].filter(
        (targetWindow): targetWindow is Window => !!targetWindow
      )
    )
  );
};

const patchResizeObserverForWindow = (targetWindow: Window) => {
  if (patchedWindows.has(targetWindow)) return;

  const windowWithResizeObserver = targetWindow as Window & {
    ResizeObserver?: typeof ResizeObserver;
  };
  const NativeResizeObserver = windowWithResizeObserver.ResizeObserver;
  if (typeof NativeResizeObserver === 'function') {
    const ResizeObserverImpl = NativeResizeObserver;

    class RafResizeObserver {
      private observer: ResizeObserver;
      private frameId: number | null = null;

      constructor(callback: ResizeObserverCallback) {
        this.observer = new ResizeObserverImpl((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
          if (this.frameId !== null) {
            targetWindow.cancelAnimationFrame(this.frameId);
          }

          this.frameId = targetWindow.requestAnimationFrame(() => {
            this.frameId = null;
            callback(entries, observer);
          });
        });
      }

      observe(target: Element, options?: ResizeObserverOptions) {
        this.observer.observe(target, options);
      }

      unobserve(target: Element) {
        this.observer.unobserve(target);
      }

      disconnect() {
        if (this.frameId !== null) {
          targetWindow.cancelAnimationFrame(this.frameId);
          this.frameId = null;
        }
        this.observer.disconnect();
      }
    }

    windowWithResizeObserver.ResizeObserver = RafResizeObserver as typeof ResizeObserver;
  }

  // 部分浏览器仍会派发 error 事件；只拦截 ResizeObserver 的已知噪音，不吞其他错误。
  targetWindow.addEventListener(
    'error',
    (event) => {
      if (!isResizeObserverLoopError(event.message)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  targetWindow.addEventListener(
    'unhandledrejection',
    (event) => {
      const reason = event.reason;
      const message =
        typeof reason === 'string'
          ? reason
          : reason instanceof Error
            ? reason.message
            : undefined;
      if (!isResizeObserverLoopError(message)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    },
    true
  );

  patchedWindows.add(targetWindow);
};

/** 安装 ResizeObserver 兼容补丁。 */
export const patchResizeObserver = () => {
  getTargetWindows().forEach(patchResizeObserverForWindow);
};

// 必须在模块加载时立即执行，确保早于 React/antd 创建 ResizeObserver。
patchResizeObserver();
